import { createContext, useContext, useEffect, useState } from "react"
import { getAuthStorage, clearAuthStorage } from "@/lib/authStorage"

type CurrentUser = {
  id: string
  name: string
  email: string
  tenant_id: string  // Multi-tenancy support
  doctor_id?: string  // Optional - receptionists don't have a doctor_id
  clinic_id: string
  role: string
}

type AuthContextType = {
  currentUser: CurrentUser | null
  setCurrentUser: (user: CurrentUser) => void
  logout: () => void
  isLoading: boolean
  tenantId: string | null  // Convenience accessor for tenant_id
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUserState] = useState<CurrentUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function initAuth() {
      try {
        const store = getAuthStorage()
        const stored = store.getItem("currentUser")
        if (!stored) {
          setIsLoading(false)
          return
        }
        const user = JSON.parse(stored)
        if (!user || !user.id || !user.clinic_id || !user.role) {
          store.removeItem("currentUser")
          setIsLoading(false)
          return
        }
        if (!user.tenant_id) user.tenant_id = 'default_tenant'

        const token = store.getItem("accessToken")
        if (!token) {
          clearAuthStorage()
          setIsLoading(false)
          return
        }

        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'
        const res = await fetch(`${baseUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (cancelled) return

        if (res.status === 401 || res.status === 403) {
          clearAuthStorage()
          setCurrentUserState(null)
        } else if (res.ok) {
          setCurrentUserState(user)
        } else {
          clearAuthStorage()
          setCurrentUserState(null)
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error initializing auth:", error)
          clearAuthStorage()
          setCurrentUserState(null)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    initAuth()
    return () => { cancelled = true }
  }, [])

  const setCurrentUser = (user: CurrentUser) => {
    try {
      if (!user.tenant_id) {
        user.tenant_id = 'default_tenant'
      }
      const store = getAuthStorage()
      store.setItem("currentUser", JSON.stringify(user))
      setCurrentUserState(user)
    } catch (error) {
      console.error("Error saving user to auth storage:", error)
    }
  }

  const logout = () => {
    try {
      clearAuthStorage()
      setCurrentUserState(null)
      window.location.href = "/login"
    } catch (error) {
      window.location.href = "/login"
      console.error("Error during logout:", error)
    }
  }

  // Convenience accessor for tenant_id
  const tenantId = currentUser?.tenant_id || null

  return (
    <AuthContext.Provider value={{ currentUser, setCurrentUser, logout, isLoading, tenantId }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
